import styled, { css } from 'styled-components';
import { resetBrowserButton } from '../Button/styles';
import { trackerTokens } from '../../tokens/components/Tracker/tokens';

const transitionCrisp = css`
  transition-duration: 60ms;
  transition-property: all;
  transition-timing-function: ease-in;
`;

const trackerDisabledBorder = direction => css`
  border-${direction}-color: ${trackerTokens.CoreBorderColorDisabled};
  border-${direction}-style: ${trackerTokens.CoreBorderStyleDisabled};
  border-${direction}-width: ${trackerTokens.CoreBorderWidthSecondary};
`;

const trackerCompleteBorder = direction => css`
  border-${direction}-color: ${trackerTokens.CoreBorderColorDefault};
  border-${direction}-style: ${trackerTokens.CoreBorderStyleComplete};
  border-${direction}-width: ${trackerTokens.CoreBorderWidthSecondary};
  border-image: none;
`;

/* @internal */
export const StyledTracker = styled.ol`
  position: relative;

  ${({ theme }) =>
    theme.trackerHorizontal &&
    css`
      display: flex;
      justify-content: center;
    `}

  ${({ theme }) =>
    !!theme.trackerMarginBottom &&
    css`
      margin-bottom: calc(${trackerTokens.CoreSpacing} * ${theme.trackerMarginBottom});
    `}
`;

/* @internal */
export const StyledTrackerItem = styled.li`
  ${({ theme }) =>
    theme.trackerHorizontal &&
    css`
      flex-basis: 200px;
    `}
`;
export const TrackerAnchor = styled.a`
  ${resetBrowserButton}
  ${transitionCrisp}
  align-items: center;
  color: ${trackerTokens.CoreTextColorDefault};
  cursor: pointer;
  display: flex;
  font-size: ${trackerTokens.CoreFontSize};
  font-weight: ${trackerTokens.CoreFontWeight};
  line-height: ${trackerTokens.CoreFontLeading};
  padding: 8px;
  position: relative;
  user-select: none;
  width: fit-content;

  &:hover {
    color: ${trackerTokens.CoreTextColorHover};
    text-decoration: none;
  }

  &:focus {
    box-shadow: ${trackerTokens.CoreShadowFocus};
  }

  ${({ theme }) =>
    theme.trackerHorizontal &&
    css`
      flex-direction: column;
      text-align: center;
      width: 100%;
    `}

  ${({ theme }) =>
    theme.trackerItemActive &&
    css`
      color: ${trackerTokens.CoreTextColorActive};
      pointer-events: none;
    `}

  ${({ theme }) =>
    theme.trackerItemCompleted &&
    css`
      color: ${trackerTokens.CoreTextColorComplete};
    `}

  ${({ theme }) =>
    theme.trackerItemDisabled &&
    css`
      color: ${trackerTokens.CoreTextColorDisabled};
      pointer-events: none;
    `}
`;

export const TrackerBullet = styled.div`
  ${transitionCrisp}
  background-color: transparent;
  border: ${trackerTokens.CoreBorderWidthPrimary} ${trackerTokens.CoreBorderStyleComplete}
    ${trackerTokens.CoreBorderColorDefault};
  border-radius: calc(8px / 2);
  flex: 0 0 auto;
  height: 8px;
  margin-right: 18px;
  pointer-events: none;
  width: 8px;
  z-index: 1;

  ${TrackerAnchor}:hover && {
    background-color: ${trackerTokens.CoreBackgroundColorHover};
    border-color: ${trackerTokens.CoreBorderColorHover};
    transform: scale(1.5);
  }

  ${({ theme }) =>
    theme.trackerHorizontal &&
    css`
      margin-bottom: 8px;
      margin-right: 0;
    `}

  ${({ theme }) =>
    theme.trackerItemCompleted &&
    css`
      background-color: ${trackerTokens.CoreBackgroundColorDefault};
      border-color: ${trackerTokens.CoreBorderColorDefault};
    `}

    ${({ theme }) =>
    theme.trackerItemDisabled &&
    css`
      border-color: ${trackerTokens.CoreBorderColorDisabled};
    `}

    ${({ theme }) =>
    theme.trackerItemActive &&
    css`
      transform: scale(1.5);
      background-color: ${trackerTokens.CoreBackgroundColorActive};
      border-color: ${trackerTokens.CoreBorderColorActive};
    `}
`;

export const TrackerContent = styled.div`
  display: none;
  padding: 12px 0;
  position: relative;

  ${({ theme }) =>
    theme.trackerContentActive &&
    css`
      display: block;
    `}
`;

const trackerLine = css`
  &::before,
  &::after {
    ${transitionCrisp}
    border: 0;
    content: '';
    left: 11px;
    pointer-events: none;
    position: absolute;

    // Safari and IE needs the line block to have a
    // specific size to display the dots properly
    // https://bugs.webkit.org/show_bug.cgi?id=155955
    //stylelint-disable order/properties-alphabetical-order
    height: auto;
    right: calc(100% - 20px);
    width: auto;
    //stylelint-enable order/properties-alphabetical-order
  }

  &::before {
    ${trackerDisabledBorder('left')}
    bottom: calc(50% + 6px);
    top: 0;
    margin-top: 1px;

    ${TrackerAnchor}:hover && {
      bottom: calc(50% + 8px);
    }
  }

  &::after {
    ${trackerDisabledBorder('left')}
    bottom: 0;
    top: calc(50% + 6px);
    margin-bottom: 1px;

    ${TrackerAnchor}:hover && {
      top: calc(50% + 8px);
    }
  }

  // Trimming back end
  ${({ theme }) =>
    theme.trackerItemFirst &&
    css`
      &::before {
        content: none;
      }
    `}

  // Trimming front end
  ${({ theme }) =>
    theme.trackerItemLast &&
    css`
      &::after {
        content: none;
      }
    `}

  ${({ theme }) =>
    theme.trackerItemDisabled &&
    css`
      &::before,
      &::after {
        ${trackerDisabledBorder('left')}
      }

      &::after {
        bottom: 0;
      }
    `}

    ${({ theme }) =>
    theme.trackerItemCompleted &&
    css`
      &::before,
      &::after {
        ${trackerCompleteBorder('left')}
      }

      &::before {
        margin-top: 0;
      }

      &::after {
        bottom: 0;
        margin-bottom: 0;
      }
    `}

  ${({ theme }) =>
    theme.trackerItemActive &&
    css`
      &::before {
        ${trackerCompleteBorder('left')}
        bottom: calc(50% + 8px);
        margin-top: 0;
      }

      &::after {
        top: calc(50% + 8px);
        margin-bottom: 0;
      }
    `}

    ${({ theme }) =>
    theme.trackerHorizontal &&
    css`
      &::before,
      &::after {
        border-left: 0;
        top: 11px;

        // Safari and IE needs the line block to have a
        // specific size to display the dots properly
        // https://bugs.webkit.org/show_bug.cgi?id=155955
        //stylelint-disable order/properties-alphabetical-order
        bottom: calc(100% - 20px);
        //stylelint-enable order/properties-alphabetical-order
      }

      &::before {
        ${trackerDisabledBorder('top')};
        left: 0;
        right: calc(50% + 6px);
        margin-left: 1px;
        margin-top: 0;

        ${TrackerAnchor}:hover && {
          right: calc(50% + 8px);
          top: 11px;
        }
      }

      &::after {
        ${trackerDisabledBorder('top')};
        left: calc(50% + 6px);
        right: 0;
        margin-right: 1px;

        ${TrackerAnchor}:hover && {
          left: calc(50% + 8px);
          top: 11px;
        }
      }
    `}
`;

export const TrackerContentLine = styled.div`
  ${trackerLine}
  &::before {
    display: none;
  }

  &::after {
    top: 0;
  }

  ${({ theme }) =>
    theme.trackerHorizontal &&
    css`
      display: none;
    `}
`;

export const TrackerLine = styled.div`
  ${trackerLine}

  ${({ theme }) =>
    theme.trackerHorizontal &&
    theme.trackerItemActive &&
    css`
      &::before {
        ${trackerCompleteBorder('top')};
        right: calc(50% + 8px);
        margin-left: 0;
      }
      &::after {
        left: calc(50% + 8px);
      }
    `}

  ${({ theme }) =>
    theme.trackerHorizontal &&
    theme.trackerItemDisabled &&
    css`
      &::before,
      &::after {
        ${trackerDisabledBorder('top')};
      }

      &::after {
        right: 0;
      }
    `}

  ${({ theme }) =>
    theme.trackerHorizontal &&
    theme.trackerItemCompleted &&
    css`
      &::before,
      &::after {
        ${trackerCompleteBorder('top')};
        margin-left: 0;
      }

      &::after {
        right: 0;
        margin-right: 0;
      }
    `}
`;

export const TrackerContentWrapper = styled.div`
  padding-left: 34px;

  ${({ theme }) =>
    theme.trackerHorizontal &&
    css`
      padding-left: 0;
    `}
`;
